/* 规则常量与公式。
 *
 * 全部来自 rules_files/ 下的 V1.2 文档，集中在此，不要在组件里另写一份：
 *   · 风控五态机与三层风险线  —— 01 策略与风控说明书 第七章
 *   · 净收益与关键价位公式    —— 03 手续费与净收益测算表
 *   · 止盈档位               —— 01 第五章
 */

/** 五态机（01 第 7.3 节）
 *
 * 键名保持英文：它就是 05_字段清单 里 `bot_state` 的取值，
 * 也是后端接口和日志里出现的字面量，改成中文会切断与系统状态的对应关系。
 * 界面上显示 `label` 中文，需要溯源时再看英文码。
 */
export const STATES = {
  NORMAL: {
    label: '正常',
    trigger: '未触发亏损线',
    allow: '按计划买入 / 卖出',
  },
  OBSERVE: {
    label: '观察',
    trigger: '净亏损达到「观察」阈值',
    allow: '监控与卖出；禁止新增主动买入',
  },
  PAUSED: {
    label: '暂停',
    trigger: '净亏损达到「暂停」阈值，或系统异常',
    allow: '撤单、监控、告警；等待人工',
  },
  PROTECT: {
    label: '保护',
    trigger: '净亏损达到「保护」阈值',
    allow: '停止主动交易；仅执行已授权风险动作',
  },
  STOPPED: {
    label: '停止',
    trigger: '已亏损离场 / 人工停止',
    allow: '仅监控、对账、复盘',
  },
}

/** 状态的中文名；拿不到映射时退回英文码，不显示空白 */
export function stateLabel(state) {
  return STATES[state]?.label ?? state
}

/* 订单枚举的中文名。
 *
 * 原来只在订单页里定义，平仓面板拿不到，于是那边的提示直接吐出
 * `状态 PENDING_NEW` 这种交易所原文。同一个枚举在界面上有两种呈现，
 * 谁都说不清哪个是对的 —— 放到这里统一。 */
export const SIDE_LABEL = { BUY: '买入', SELL: '卖出' }
export const TYPE_LABEL = { LIMIT: '限价', MARKET: '市价', LIMIT_MAKER: '只挂单' }
export const ORDER_STATUS_LABEL = {
  NEW: '待成交',
  PENDING_NEW: '提交中',
  PARTIALLY_FILLED: '部分成交',
  FILLED: '已成交',
  CANCELED: '已撤销',
  REJECTED: '已拒绝',
  UNKNOWN: '状态未知',
}

export const sideLabel = (v) => SIDE_LABEL[v] ?? v ?? '—'
export const typeLabel = (v) => TYPE_LABEL[v] ?? v ?? '—'
export const orderStatusLabel = (v) => ORDER_STATUS_LABEL[v] ?? v ?? '—'

export const STATE_ORDER = ['NORMAL', 'OBSERVE', 'PAUSED', 'PROTECT', 'STOPPED']

/** 状态严重度比较：取更严重的那个 */
export function worstState(a, b) {
  return STATE_ORDER.indexOf(a) >= STATE_ORDER.indexOf(b) ? a : b
}

/* 止盈档位与低吸偏移原本在这里各写了一份常量。
 *
 * 现在盘前计划由后端每日生成，档位、参考价、止盈目标、价格底线全部随
 * plan 块下发 —— 前端再留一份就是第二个真值源，改规则时必然漏掉一边。
 * 需要这些值请读 `usePlanStore()`，不要在组件里重新定义。 */

/**
 * 行情过期阈值。
 *
 * ⚠️ 沙盒行情清淡，几十秒不更新是常态，用生产的 15s 会持续误判为断流、
 * 把机器人锁死在 PAUSED。与后端推流的 stale_after_ms 保持一致（60s）。
 * 上生产前必须按实际更新频率重新标定，定松了会漏掉真断流。
 */
/* 行情过期阈值已移交后端的规则档案（profiles.py），按环境各存一份：
 *   沙盒 60s —— 沙盒几十秒不成交是常态，用 15s 会长期锁死在暂停
 *   生产 15s —— 规则书 V1.2 的生产值，实测生产行情连续
 *
 * 前端曾在这里写死 60000。切到生产后界面还按 60 秒判断断流，
 * 而后端已按 15 秒在判 —— 两边对同一件事给出不同结论，最难查。
 * 现在统一读 `useConsoleStore().staleLimitMs`。
 */

// --- 净收益测算（03 手续费与净收益测算表，公式逐条对齐） ---

/** 净卖出比例：1 − 卖出费率 − 滑点率。关键价位全部由它反推。 */
export const netFactor = (fees) => 1 - fees.sell - fees.slip

export function calcPnl({ avgPrice, buyAmount, sellPrice, fees }) {
  const qty = buyAmount / avgPrice
  const buyFee = buyAmount * fees.buy
  const gross = qty * sellPrice
  const sellFee = gross * fees.sell
  const slip = gross * fees.slip
  const netProceeds = gross - sellFee - slip
  const netPnl = netProceeds - buyAmount - buyFee
  return { qty, buyFee, gross, sellFee, slip, netProceeds, netPnl, pnlRate: netPnl / buyAmount }
}

/** 由目标净盈亏反推卖出价：(成本 + 买入费 + 目标) ÷ (数量 × 净卖出比例) */
export function priceForNet({ avgPrice, buyAmount, fees }, targetNet) {
  const qty = buyAmount / avgPrice
  const buyFee = buyAmount * fees.buy
  return (buyAmount + buyFee + targetNet) / (qty * netFactor(fees))
}
